/**
 * Customer Manager API Client
 *
 * Wrapper functions for all customer endpoints.
 * Uses the shared KodeonApiClient for authenticated HTTP requests.
 */

import { api } from '@/lib/api'
import {
  effectiveLastSeenAt,
  effectiveStatus,
  extractDomain as extractDomainFromHelpers,
  primaryConsumerId,
} from './display'
import type {
  Customer,
  CustomerListResponse,
  CustomerCreateRequest,
  CustomerUpdateRequest,
  CustomerNarrative,
  CustomerOpportunity,
  CustomerEventsResponse,
  CustomerPurchasesResponse,
  CustomerSourceRecordsResponse,
  CustomerFilters,
  CustomerSortState,
  CreateEntitlementGrantRequest,
  EntitlementGrant,
  EntitlementListResponse,
  DeletionQueue,
} from './types'

// ── API Paths ───────────────────────────────────────────────────────────────

const PATHS = {
  customers: '/v1/customers',
  customer: (id: string) => `/v1/customers/${id}`,
  narrative: (id: string) => `/v1/customers/${id}/narrative`,
  opportunity: (id: string) => `/v1/customers/${id}/opportunity`,
  events: (id: string) => `/v1/admin/customers/${id}/events`,
  purchases: (id: string) => `/v1/admin/customers/${id}/purchases`,
  sourceRecords: (id: string) => `/v1/admin/customers/${id}/source-records`,
  // Agent-driven regeneration endpoints (X-Staff header is auto-injected by
  // the gateway based on the consumer's scopes — Stream doesn't need to set
  // it itself; see services/api/backend/internal/proxy/reverse_proxy.go).
  regenerateProfile: '/v1/agents/narrative_synthesis/generate',
  generateInsight: '/v1/agents/insight_generator/generate',
  // Catalog: list available entitlements for the grant picker.
  entitlements: '/v1/entitlements',
  // Identity admin endpoints for granting / revoking entitlements.
  entitlementGrants: '/v1/admin/entitlement-grants',
  revokeEntitlementGrant: (id: string) =>
    `/v1/admin/entitlement-grants/${id}/revoke`,
} as const

// ── List Customers ──────────────────────────────────────────────────────────

export interface ListCustomersParams {
  filters?: CustomerFilters
  sort?: CustomerSortState
  limit?: number
  cursor?: string
}

/**
 * List customers with optional filtering and pagination.
 *
 * The customer list endpoint uses the `page_size` / `page_token` opaque-cursor
 * convention (task 2808). The `cursor` parameter on `ListCustomersParams` is
 * an alias for the server-issued `next_page_token`; we map it onto the
 * `page_token` query parameter.
 */
export async function listCustomers(
  params: ListCustomersParams = {}
): Promise<CustomerListResponse> {
  const queryParams: Record<string, string | number | boolean> = {}

  // Pagination — customer list endpoint accepts page_size + page_token.
  if (params.limit) queryParams.page_size = params.limit
  if (params.cursor) queryParams.page_token = params.cursor

  // Default to last_seen_at:desc so the directory shows the most recently
  // active customers first. Operators see today's signups at the top of the
  // list as soon as they authenticate (task 3515a).
  //
  // If a combined filter + sort combination is unsupported by the backend
  // (Firestore composite-index not yet created), the backend returns 400.
  // Callers that need a filter-safe fallback should catch that case and
  // retry without the sort parameter.
  queryParams.sort = 'last_seen_at:desc'

  // Search
  if (params.filters?.q) {
    // Backend uses email param for search - adapt as needed
    queryParams.email = params.filters.q
  }

  // Note: Additional filters (status, domain, dates) may require
  // backend support. For now, we apply them client-side.

  return api.get<CustomerListResponse>(PATHS.customers, queryParams)
}

// ── Search Customers ────────────────────────────────────────────────────────

/**
 * Multi-identifier customer search via GET /v1/customers/search.
 *
 * Pass any raw identifier — email, customer ID (with or without cust_ prefix),
 * Stripe ID (cus_…), RevenueCat App User ID, $RCAnonymousID, Apple, Google, or
 * Firebase identifiers — and the backend expands it into all plausible candidate
 * keys and runs indexed Firestore array-contains lookups against the
 * `external_identifier_ids` denormalised array. This is a single O(log n) query
 * per candidate key, not a full-table scan.
 *
 * Returns up to 25 results. `has_more` is true when the cap was hit.
 * Results are deduplicated by customer ID, so a shared email alias that appears
 * on two distinct customer records will surface both.
 *
 * Requires staff-level access (IsStaff=true on the API key). Stream operator
 * keys satisfy this requirement.
 */
export async function searchCustomers(q: string): Promise<CustomerListResponse> {
  return api.get<CustomerListResponse>('/v1/customers/search', { q })
}

// ── Get Customer ────────────────────────────────────────────────────────────

/**
 * Get a single customer by ID.
 * Expands features and entitlements by default so the detail view can display them.
 *
 * The backend uses the `expand[]=<field>.data` convention to inline expandable
 * sub-resource lists. See `services/identity/api/openapi.yaml` and the
 * CustomerExpandableFields registry in `packages/go/identity/customer.go`.
 */
export async function getCustomer(id: string): Promise<Customer> {
  return api.get<Customer>(PATHS.customer(id), {
    'expand[]': ['features.data', 'entitlements.data'],
  })
}

// ── Create Customer ─────────────────────────────────────────────────────────

/**
 * Create a new customer.
 */
export async function createCustomer(
  data: CustomerCreateRequest
): Promise<Customer> {
  return api.post<Customer>(PATHS.customers, data)
}

// ── Update Customer ─────────────────────────────────────────────────────────

/**
 * Update a customer's profile.
 */
export async function updateCustomer(
  id: string,
  data: CustomerUpdateRequest
): Promise<Customer> {
  return api.patch<Customer>(PATHS.customer(id), data)
}

// ── Customer Narrative ──────────────────────────────────────────────────────

/**
 * Get customer narrative.
 * Returns null if no narrative exists (404 is expected).
 */
export async function getNarrative(
  id: string
): Promise<CustomerNarrative | null> {
  try {
    return await api.get<CustomerNarrative>(PATHS.narrative(id))
  } catch (error) {
    // 404 means no narrative exists yet - this is expected
    if (error instanceof Error && error.message.includes('404')) {
      return null
    }
    throw error
  }
}

/**
 * Update customer narrative.
 */
export async function updateNarrative(
  id: string,
  narrative: CustomerNarrative
): Promise<CustomerNarrative> {
  return api.put<CustomerNarrative>(PATHS.narrative(id), narrative)
}

// ── Customer Opportunity ────────────────────────────────────────────────────

/**
 * Get customer opportunity record.
 * Returns null if no opportunity exists (404 is expected).
 */
export async function getOpportunity(
  id: string
): Promise<CustomerOpportunity | null> {
  try {
    return await api.get<CustomerOpportunity>(PATHS.opportunity(id))
  } catch (error) {
    // 404 means no opportunity exists yet - this is expected
    if (error instanceof Error && error.message.includes('404')) {
      return null
    }
    throw error
  }
}

/**
 * Update customer opportunity record.
 */
export async function updateOpportunity(
  id: string,
  opportunity: CustomerOpportunity
): Promise<CustomerOpportunity> {
  return api.put<CustomerOpportunity>(PATHS.opportunity(id), opportunity)
}

// ── Customer Events ─────────────────────────────────────────────────────────

export interface GetCustomerEventsParams {
  from?: string // ISO date
  to?: string // ISO date
  limit?: number
  cursor?: string
}

/**
 * Get customer event timeline.
 */
export async function getCustomerEvents(
  id: string,
  params: GetCustomerEventsParams = {}
): Promise<CustomerEventsResponse> {
  const queryParams: Record<string, string | number> = {}

  if (params.from) queryParams.from = params.from
  if (params.to) queryParams.to = params.to
  if (params.limit) queryParams.limit = params.limit
  if (params.cursor) queryParams.starting_after = params.cursor

  return api.get<CustomerEventsResponse>(PATHS.events(id), queryParams)
}

// ── Customer Purchases ──────────────────────────────────────────────────────

/**
 * Get customer purchase history.
 */
export async function getCustomerPurchases(
  id: string,
  limit?: number,
  cursor?: string
): Promise<CustomerPurchasesResponse> {
  const queryParams: Record<string, string | number> = {}

  if (limit) queryParams.limit = limit
  if (cursor) queryParams.starting_after = cursor

  return api.get<CustomerPurchasesResponse>(PATHS.purchases(id), queryParams)
}

// ── Customer Source Records ─────────────────────────────────────────────────

/**
 * Get customer source records (external system data).
 */
export async function getCustomerSourceRecords(
  id: string
): Promise<CustomerSourceRecordsResponse> {
  return api.get<CustomerSourceRecordsResponse>(PATHS.sourceRecords(id))
}

// ── Batch Fetch for Detail View ─────────────────────────────────────────────

export interface CustomerDetailBatch {
  customer: Customer | null
  narrative: CustomerNarrative | null
  opportunity: CustomerOpportunity | null
  errors: Record<string, string>
}

/**
 * Fetch customer with narrative and opportunity in parallel.
 * Used for inspector detail view.
 */
export async function fetchCustomerDetail(
  id: string
): Promise<CustomerDetailBatch> {
  const errors: Record<string, string> = {}

  const results = await Promise.allSettled([
    getCustomer(id),
    getNarrative(id),
    getOpportunity(id),
  ])

  const getValue = <T>(
    result: PromiseSettledResult<T>,
    key: string
  ): T | null => {
    if (result.status === 'fulfilled') {
      return result.value
    }
    errors[key] =
      result.reason instanceof Error
        ? result.reason.message
        : String(result.reason)
    return null
  }

  return {
    customer: getValue(results[0], 'customer'),
    narrative: getValue(results[1], 'narrative'),
    opportunity: getValue(results[2], 'opportunity'),
    errors,
  }
}

// ── Export Helper ───────────────────────────────────────────────────────────

/**
 * Export row shape for CSV export.
 *
 * Only includes fields that actually exist on the backend Customer record
 * (or are deterministically derivable from one). No invented columns.
 */
export interface CustomerExportRow {
  id: string
  email: string
  name: string
  status: string
  created: string
  last_seen_at: string
  domain: string
  primary_consumer: string
  email_verified: string
  delinquent: string
  livemode: string
}

/**
 * Fetch all customers matching filters for CSV export.
 * Returns typed rows ready for exportToCsv().
 */
export async function fetchCustomersForExport(
  filters?: CustomerFilters
): Promise<CustomerExportRow[]> {
  // Fetch all customers (pagination handled)
  const allCustomers: Customer[] = []
  let cursor: string | undefined
  let hasMore = true

  while (hasMore) {
    const response = await listCustomers({
      filters,
      limit: 100,
      cursor,
    })
    allCustomers.push(...response.data)
    hasMore = response.has_more
    // Cursor is the server-issued next_page_token (task 2808). Fall back
    // to undefined when the token is missing/empty so the loop terminates.
    cursor =
      response.next_page_token && response.next_page_token.length > 0
        ? response.next_page_token
        : undefined
    if (!cursor) {
      hasMore = false
    }
  }

  return allCustomers.map((c) => {
    const lastSeen = effectiveLastSeenAt(c)
    return {
      id: c.id,
      email: c.email,
      name: c.name ?? '',
      status: effectiveStatus(c),
      created: c.created
        ? new Date(c.created * 1000).toISOString()
        : '',
      last_seen_at: lastSeen ? new Date(lastSeen * 1000).toISOString() : '',
      domain: extractDomainFromHelpers(c.email),
      primary_consumer: primaryConsumerId(c) ?? '',
      email_verified: c.email_verified ? 'true' : 'false',
      delinquent: c.delinquent ? 'true' : 'false',
      livemode: c.livemode ? 'true' : 'false',
    }
  })
}

// ── Profile Regeneration (Narrative + Opportunity) ──────────────────────────

/**
 * Response shape for `/v1/agents/narrative_synthesis/generate`.
 * Mirrors the Pydantic `NarrativeOperationResponse` on the agents service.
 *
 * The agent regenerates BOTH the customer narrative AND the opportunity
 * record in a single synthesis pass (per its SOUL.md), so a single button
 * "Generate Narrative" maps cleanly to this endpoint.
 */
export interface RegenerateProfileResponse {
  success: boolean
  action: string
  message: string
  agent_response?: string | null
  duration_ms: number
}

/**
 * Trigger a fresh narrative + opportunity synthesis for the given customer.
 *
 * The agent reads recent events, applies the latest narrative_synthesis logic,
 * and persists the new narrative + opportunity records. Operation typically
 * takes 30-90 seconds.
 */
export async function regenerateProfile(
  customerId: string,
): Promise<RegenerateProfileResponse> {
  return api.post<RegenerateProfileResponse>(PATHS.regenerateProfile, {
    customer_id: customerId,
  })
}

// ── Insight Generation ──────────────────────────────────────────────────────

/**
 * Request shape for `/v1/agents/insight_generator/generate`.
 * `app_id` is required by the backend — for an operator-triggered insight we
 * default to the customer's acquisition app, falling back to `app_goals`.
 */
export interface GenerateInsightParams {
  app_id: string
  event_lookback_days?: number
  event_limit?: number
  trigger_type?: 'scheduled' | 'manual' | 'event'
  channel_preference?: 'app' | 'email' | 'any'
  include_customer_profile?: boolean
  /** When true, the agent decides what insight it would send but does NOT deliver it. */
  dry_run?: boolean
}

/**
 * Response shape for `/v1/agents/insight_generator/generate`.
 */
export interface GenerateInsightResponse {
  success: boolean
  action: 'generated' | 'declined' | 'error' | string
  insight_id?: string | null
  delivery_channel?: string | null
  decline_reason?: string | null
  message: string
  agent_response?: string | null
  duration_ms: number
  dry_run: boolean
}

/**
 * Trigger a fresh insight generation for the given customer.
 *
 * Insights are short, action-oriented prompts surfaced to the customer based
 * on recent activity. The agent may decline to send an insight if the
 * customer has been over-messaged or no pattern is found.
 */
export async function generateInsight(
  customerId: string,
  params: GenerateInsightParams,
): Promise<GenerateInsightResponse> {
  return api.post<GenerateInsightResponse>(PATHS.generateInsight, {
    customer_id: customerId,
    app_id: params.app_id,
    event_lookback_days: params.event_lookback_days ?? 30,
    event_limit: params.event_limit ?? 500,
    trigger_type: params.trigger_type ?? 'manual',
    channel_preference: params.channel_preference ?? 'any',
    include_customer_profile: params.include_customer_profile ?? false,
    dry_run: params.dry_run ?? false,
  })
}

// ── Catalog: Entitlements ───────────────────────────────────────────────────

/**
 * List all available entitlements from the catalog.
 *
 * Used by the Grant Entitlement dialog to populate the entitlement picker.
 * The catalog endpoint requires `catalog:read` scope which all operator API
 * keys carry.
 */
export async function listCatalogEntitlements(): Promise<EntitlementListResponse> {
  return api.get<EntitlementListResponse>(PATHS.entitlements)
}

// ── Entitlement Grants (admin) ──────────────────────────────────────────────

/**
 * Grant an entitlement to a customer.
 *
 * Calls `POST /v1/admin/entitlement-grants`. Requires `staff:all` scope.
 * The created grant takes effect immediately (or at `valid_from` if specified)
 * and expires at `valid_to`. Granting an entitlement transitively grants every
 * feature contained in that entitlement — there is no separate "grant feature"
 * API; features are always granted by virtue of belonging to a granted
 * entitlement.
 */
export async function grantEntitlement(
  body: CreateEntitlementGrantRequest,
): Promise<EntitlementGrant> {
  return api.post<EntitlementGrant>(PATHS.entitlementGrants, body)
}

/**
 * Revoke an existing entitlement grant.
 *
 * Calls `POST /v1/admin/entitlement-grants/{id}/revoke`. The grant is not
 * deleted — its `valid_to` is set to now and it is marked as revoked,
 * preserving the audit trail.
 */
export async function revokeEntitlementGrant(
  grantId: string,
): Promise<EntitlementGrant> {
  return api.post<EntitlementGrant>(
    PATHS.revokeEntitlementGrant(grantId),
    {},
  )
}

// ── Deletions Queue ──────────────────────────────────────────────────────────

/**
 * Fetch the account-deletion queue, grouped server-side into three lifecycle
 * buckets (ready / cooldown / deleted). Staff-only; Stream operator keys carry
 * the required user:read scope.
 *
 * GET /v1/customers/deletions
 */
export async function listDeletions(): Promise<DeletionQueue> {
  return api.get<DeletionQueue>('/v1/customers/deletions')
}

/**
 * Operator action: cancel a pending account deletion for the given customer,
 * pulling them out of the cooldown queue. Requires user:write (Stream operator
 * keys satisfy this). Unlike the self-service cancel, the customer is targeted
 * by id rather than the caller's identity.
 *
 * POST /v1/customers/{customerId}/cancel-deletion
 */
export async function cancelDeletion(customerId: string): Promise<void> {
  await api.post(`/v1/customers/${customerId}/cancel-deletion`, {})
}

// ── Domain Extraction Helper (re-export) ────────────────────────────────────

/**
 * Extract domain from email address. Re-exported from `display.ts` so
 * existing imports from `./api` continue to work.
 */
export function extractDomain(email: string): string {
  return extractDomainFromHelpers(email)
}
